import { Registerable } from '@baserow/modules/core/registry'
import { brandedMessage } from '@baserow/modules/core/head'

/**
 * Translates a brand-facing step title. `this.app` is the Nuxt app, so the
 * configured name is read from `$config.public` (what `useRuntimeConfig()`
 * returns) rather than importing from `#app` into this registry module.
 */
const brandedStepMessage = (app, key) =>
  brandedMessage(
    // `app` is the Nuxt app, whose `$i18n` is the Composer, so it does carry
    // `t` (unlike a component instance's `$i18n`). Bound because the Composer's
    // `t` reads its own internal state off `this`.
    (...args) => app.$i18n.t(...args),
    app.$config?.public?.baserowBrandName,
    key
  )

export class GuidedTourType extends Registerable {
  /**
   * Indicates the order in which the steps of multiple guided tours should be shown,
   * if they're active.
   */
  get order() {
    return 100
  }

  /**
   * Indicates whether the completed state should be saved for this tour. If set to
   * true, then it will be saved and the tour never starts again. If set to false, then
   * the tour purely depends on the `isActive` state whether it's shown.
   */
  get saveCompleted() {
    return true
  }

  /**
   * Hook that is called when whole onboarding completes, and this one was included.
   */
  completed() {}

  /**
   * Indicates whether this tour must be included when the user manually replays the
   * guided tour. Tours that must only be seen the first time, like one-off
   * announcements, can return false here.
   */
  get showOnReplay() {
    return true
  }

  /**
   * Should return true if the guided tour should is active and should be shown. This
   * is a reactive method. If multiple guided tours are `true`, then they will automatically be combined.
   */
  isActive(route) {
    throw new Error('The GuidedTourType.isActive method must be implemented.')
  }
}

export class GuidedTourStep {
  constructor(
    app,
    title = null,
    content = null,
    selector = null,
    position = 'right-top',
    highlightPadding = 2
  ) {
    this.app = app
    this._title = title
    this._content = content
    this._selector = selector
    this._position = position
    this._highlightPadding = highlightPadding
  }

  /**
   * If set, it will be shown as title at the top of the visual step.
   */
  get title() {
    return this._title
  }

  /**
   * Markdown content at the body of the step.
   */
  get content() {
    if (this._content === null) {
      throw new Error('The content must be set in a guided tour step.')
    }
    return this._content
  }

  /**
   * Can contain one or more selectors that must be highlighted. If multiple are
   * provided, the elements must be directly next to each other.
   */
  get selector() {
    return this._selector
  }

  /**
   * Indicates where the step must be displayed. If `POSITION_CENTER`, no elements
   * will be highlighted. Anything else, and the element will be placed related to
   * the selected elements by the selector.
   */
  get position() {
    return this._position
  }

  /**
   * If null is returned, then the default button text will be used.
   */
  get buttonText() {
    return null
  }

  /**
   * If set to true, then step will be skipped if it's not the first one. This can be
   * used if multiple guided tours are shown combined, and the second has a welcome
   * message that is redundant if the first one also has a welcome message.
   */
  get skipIfNotFirst() {
    return false
  }

  get highlightPadding() {
    return this._highlightPadding
  }

  /**
   * Indicates whether this step must be included when the user manually replays the
   * guided tour. Steps that must only be seen the first time can return false here.
   */
  get showOnReplay() {
    return true
  }

  /**
   * Can optionally contain Baserow academy YouTube video ids related to this step. The
   * first one is shown as a thumbnail in the step, and clicking it plays all of them in
   * the given order.
   */
  get videos() {
    return []
  }

  /**
   * Hook that is called before the step is shown. This can be used to open a context
   * menu, for example.
   */
  beforeShow() {}

  /**
   * Hook that is called after the step is shown. This can be used to close a context
   * menu, for example.
   */
  afterShow() {}
}

class WelcomeGuidedTourStep extends GuidedTourStep {
  get title() {
    return brandedStepMessage(this.app, 'welcomeGuidedTourStep.title')
  }

  get content() {
    const { $i18n: i18n } = this.app
    return i18n.t('welcomeGuidedTourStep.content')
  }

  get selectors() {
    return []
  }

  get position() {
    return 'center'
  }

  get buttonText() {
    const { $i18n: i18n } = this.app
    return i18n.t('welcomeGuidedTourStep.buttonText')
  }

  get skipIfNotFirst() {
    return true
  }
}

class ControlCenterGuidedTourStep extends GuidedTourStep {
  get title() {
    return brandedStepMessage(this.app, 'controlCenterGuidedTourStep.title')
  }

  get content() {
    const { $i18n: i18n } = this.app
    return i18n.t('controlCenterGuidedTourStep.content')
  }

  get selectors() {
    return ['[data-highlight="workspaces"]', '[data-highlight="menu"]']
  }

  get position() {
    return 'right-top'
  }

  get videos() {
    return ['aXZzQRRTt9c']
  }
}

class CreateNewGuidedTourStep extends GuidedTourStep {
  get title() {
    const { $i18n: i18n } = this.app
    return i18n.t('createNewGuidedTourStep.title')
  }

  get content() {
    const { $i18n: i18n } = this.app
    return i18n.t('createNewGuidedTourStep.content')
  }

  get selectors() {
    return ['[data-highlight="create-new"]']
  }

  get position() {
    return 'right-bottom'
  }

  get videos() {
    return ['KNK1rgK3bJY']
  }
}

export class SidebarGuidedTourType extends GuidedTourType {
  static getType() {
    return 'sidebar'
  }

  get steps() {
    return [
      new WelcomeGuidedTourStep(this.app),
      new ControlCenterGuidedTourStep(this.app),
      new CreateNewGuidedTourStep(this.app),
    ]
  }

  get order() {
    return 100
  }

  isActive() {
    return true
  }
}
